/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// symbols.js — the LOGICAL symbol of each chip, derived from its catalog def
// exactly the way footprints.js derives seated holes: pure data, no per-chip
// drawing code. The schematic view (Feature 150) draws a box with named pin
// stubs grouped by side, and the layout router connects the stubs by net.
//
// Grouping rules (deterministic, from the def's pins + Feature 130 pinGroups):
//   • VCC → a stub on the TOP edge, GND → a stub on the BOTTOM edge (real
//     schematics drop rail symbols at these rather than route them),
//   • a `pinGroups` bus collapses its whole run into ONE bus stub (dir in →
//     left, out → right, io → left),
//   • every remaining functional pin is one stub: input/io on the LEFT,
//     output on the RIGHT,
//   • NC pins are omitted — a schematic shows only what carries signal.
//
// A chip whose `logic.units` are all one gate primitive also carries a
// distinctive-shape `glyph` (AND/OR/NAND/NOR/XOR/XNOR/NOT/BUFFER) the view draws as
// a small type badge; multi-function parts (decoders, counters, sequential)
// are a plain labelled box. The glyph is keyed off the SAME `logic.units` the
// evaluator walks, so it can never disagree with the simulated behavior.

import { CHIP_DEFS, chipDef, chipMarking, partDef } from "./index.js";

/** Which box edge a functional pin/bus of a given role/direction sits on. */
function sideForRole(role) {
  return role === "output" ? "right" : "left"; // input & io read on the left
}

/** A bus tap's electrical role, from its `pinGroups` direction. */
function roleForBusDir(dir) {
  if (dir === "out") return "output";
  if (dir === "io") return "io";
  return "input";
}

/** Gate-primitive fn → distinctive-shape glyph name, or null (no shape). */
const GLYPH_OF_FN = Object.freeze({
  AND: "AND",
  OR: "OR",
  NAND: "NAND",
  NOR: "NOR",
  XOR: "XOR",
  XNOR: "XNOR",
  INV: "NOT",
  BUF: "BUFFER",
  BUF3: "BUFFER",
});

/**
 * The distinctive-shape glyph for a def, or null. Only a chip whose every
 * combinational unit is the SAME gate primitive earns a glyph (a quad NAND is
 * still "a NAND"); mixed or `COMB`/sequential parts stay a plain box.
 */
export function glyphOf(def) {
  const units = def?.logic?.units;
  if (!units?.length) return null; // sequential parts have no units
  const fns = new Set(units.map((u) => u.fn));
  if (fns.size !== 1) return null;
  return GLYPH_OF_FN[units[0].fn] ?? null;
}

/**
 * Build the logical symbol record for a chip def. Pure: derives from the def's
 * `pins` + `pinGroups` only, never mutates it.
 *
 * @param {object} def - a catalog chip def.
 * @returns {{ id, label, title, glyph, sides:{left,right,top,bottom} }}
 *   Each side is an ordered list of stubs; a stub is either
 *   `{ kind:"pin", pin, name, role }` or
 *   `{ kind:"bus", name, pins, dir, role }`.
 */
export function buildSymbol(def) {
  const sides = { left: [], right: [], top: [], bottom: [] };

  // First member of a pinGroup → the group; every later member is skipped so
  // the whole run collapses to one bus stub.
  const groupOfPin = new Map();
  for (const g of def.pinGroups ?? []) {
    for (const pin of g.pins) if (!groupOfPin.has(pin)) groupOfPin.set(pin, g);
  }
  const emittedGroups = new Set();

  for (const p of def.pins) {
    if (p.role === "vcc") {
      sides.top.push({ kind: "pin", pin: p.n, name: p.name, role: p.role });
      continue;
    }
    if (p.role === "gnd") {
      sides.bottom.push({ kind: "pin", pin: p.n, name: p.name, role: p.role });
      continue;
    }
    if (p.role === "nc") continue; // omit no-connects from the symbol

    const group = groupOfPin.get(p.n);
    if (group) {
      if (emittedGroups.has(group)) continue; // already represented
      emittedGroups.add(group);
      const role = roleForBusDir(group.dir);
      sides[sideForRole(role)].push({
        kind: "bus",
        name: group.name,
        pins: [...group.pins],
        dir: group.dir,
        role,
      });
      continue;
    }
    sides[sideForRole(p.role)].push({
      kind: "pin",
      pin: p.n,
      name: p.name,
      role: p.role,
    });
  }

  return {
    id: def.id,
    label: chipMarking(def),
    title: def.title,
    kind: "chip",
    glyph: glyphOf(def),
    sides,
  };
}

// ── Non-chip parts: distinctive-shape symbols + boxes (Feature 150) ──────────
//
// A discrete or brick renders either as a distinctive SCHEMATIC SHAPE (a diode
// triangle, a resistor body, a switch, a source) with a fixed set of TERMINALS,
// or — for the many-pinned displays / resistor array — as a plain labelled box
// like a chip. A terminal references a pin number (`pin`, a seated discrete) or
// a brick terminal id (`terminal`, a PSU/clock pad); the geometry lives in
// schematic-layout.js, exactly as the chip box geometry does.

/** id → distinctive shape + its terminals (side + the pin/terminal it maps). */
const SHAPE_DEFS = Object.freeze({
  led: {
    shape: "led",
    terminals: [
      { name: "A", side: "top", pin: 1 },
      { name: "K", side: "bottom", pin: 2 },
    ],
  },
  resistor: {
    shape: "resistor",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "2", side: "right", pin: 2 },
    ],
  },
  // The track left to right like a resistor's body, the wiper's arrow coming
  // down onto it from above.
  pot: {
    shape: "potentiometer",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "3", side: "right", pin: 3 },
      { name: "W", side: "top", pin: 2 },
    ],
  },
  "cap-ceramic": {
    shape: "capacitor",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "2", side: "right", pin: 2 },
    ],
  },
  "cap-electrolytic": {
    shape: "capacitor-polarized",
    terminals: [
      { name: "+", side: "left", pin: 1 },
      { name: "-", side: "right", pin: 2 },
    ],
  },
  // A diode stands like the LED, anode on top; a Zener is the same with the
  // bent cathode bar.
  diode: {
    shape: "diode",
    terminals: [
      { name: "A", side: "top", pin: 1 },
      { name: "K", side: "bottom", pin: 2 },
    ],
  },
  zener: {
    shape: "zener",
    terminals: [
      { name: "A", side: "top", pin: 1 },
      { name: "K", side: "bottom", pin: 2 },
    ],
  },
  // A bridge rectifier as the textbook's diamond of four diodes: the AC pins
  // at its left and right corners, + at the top (where two cathodes meet), −
  // at the bottom.
  "bridge-rectifier": {
    shape: "bridge",
    terminals: [
      { name: "~", side: "left", pin: 4 },
      { name: "~", side: "right", pin: 3 },
      { name: "+", side: "top", pin: 1 },
      { name: "-", side: "bottom", pin: 2 },
    ],
  },
  inductor: {
    shape: "inductor",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "2", side: "right", pin: 2 },
    ],
  },
  // A transistor as the textbook draws it: the base or gate in from the
  // left, the switched pins out of the top and bottom on the right — the one
  // current flows in at on top (an NPN's collector, a PNP's emitter, an
  // N-channel's drain, a P-channel's source). Pins are by NUMBER, so a part
  // turned end-for-end on the desk draws the same.
  npn: {
    shape: "npn",
    terminals: [
      { name: "B", side: "left", pin: 2 },
      { name: "C", side: "top", pin: 3, offset: 0.8 },
      { name: "E", side: "bottom", pin: 1, offset: 0.8 },
    ],
  },
  pnp: {
    shape: "pnp",
    terminals: [
      { name: "B", side: "left", pin: 2 },
      { name: "E", side: "top", pin: 1, offset: 0.8 },
      { name: "C", side: "bottom", pin: 3, offset: 0.8 },
    ],
  },
  nmos: {
    shape: "nmos",
    terminals: [
      { name: "G", side: "left", pin: 2 },
      { name: "D", side: "top", pin: 3, offset: 0.8 },
      { name: "S", side: "bottom", pin: 1, offset: 0.8 },
    ],
  },
  pmos: {
    shape: "pmos",
    terminals: [
      { name: "G", side: "left", pin: 2 },
      { name: "S", side: "top", pin: 1, offset: 0.8 },
      { name: "D", side: "bottom", pin: 3, offset: 0.8 },
    ],
  },
  "sw-slide": {
    shape: "switch",
    terminals: [
      { name: "C", side: "left", pin: 2 },
      { name: "1", side: "right", pin: 1, offset: -1 },
      { name: "2", side: "right", pin: 3, offset: 1 },
    ],
  },
  "sw-push": {
    shape: "button",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "2", side: "right", pin: 2 },
    ],
  },
  // The 6 mm tactile button: a push button with BOTH legs of each side drawn,
  // the pair joined by the switch's own strip.
  "sw-tact": {
    shape: "tact",
    terminals: [
      { name: "1", side: "left", pin: 1, offset: -0.7 },
      { name: "4", side: "left", pin: 4, offset: 0.7 },
      { name: "2", side: "right", pin: 2, offset: -0.7 },
      { name: "3", side: "right", pin: 3, offset: 0.7 },
    ],
  },
  // Two SPDT poles, levers tied: each common on the left, its A throw above
  // and its B throw below on the right.
  "sw-dpdt": {
    shape: "dpdt",
    terminals: [
      { name: "C", side: "left", pin: 2, offset: -1.8 },
      { name: "A", side: "right", pin: 1, offset: -2.8 },
      { name: "B", side: "right", pin: 3, offset: -0.8 },
      { name: "C", side: "left", pin: 5, offset: 1.8 },
      { name: "A", side: "right", pin: 6, offset: 0.8 },
      { name: "B", side: "right", pin: 4, offset: 2.8 },
    ],
  },
  "sw-toggle": {
    shape: "button",
    terminals: [
      { name: "1", side: "left", pin: 1 },
      { name: "2", side: "right", pin: 2 },
    ],
  },
  psu: {
    shape: "psu",
    terminals: [
      { name: "+", side: "top", terminal: "+" },
      { name: "−", side: "bottom", terminal: "-" },
    ],
  },
  clock: {
    shape: "clock",
    terminals: [
      { name: "clk", side: "right", terminal: "out" },
      // Its supply, on top like the PSU's own `+`: a clock runs from the
      // rails like any instrument.
      { name: "vcc", side: "top", terminal: "vcc" },
      { name: "gnd", side: "bottom", terminal: "gnd" },
    ],
  },
  // A speaker: the magnet's box and its cone, + on top as an LED's anode is.
  speaker: {
    shape: "speaker",
    terminals: [
      { name: "+", side: "top", pin: 1 },
      { name: "−", side: "bottom", pin: 2 },
    ],
  },
  // The electronic load: a current sink, + on top as the supply it draws from.
  load: {
    shape: "load",
    terminals: [
      { name: "+", side: "top", terminal: "pos" },
      { name: "−", side: "bottom", terminal: "neg" },
    ],
  },
  // Mains: the circle with a sine, L on top as a supply's + is.
  ac: {
    shape: "ac",
    terminals: [
      { name: "L", side: "top", terminal: "L" },
      { name: "N", side: "bottom", terminal: "N" },
    ],
  },
  // A transformer: its primary's two ends on the left, its secondary's on
  // the right, each winding's 1 end on top.
  transformer: {
    shape: "transformer",
    terminals: [
      { name: "P1", side: "left", terminal: "P1", offset: -1.4 },
      { name: "P2", side: "left", terminal: "P2", offset: 1.4 },
      { name: "S1", side: "right", terminal: "S1", offset: -1.4 },
      { name: "S2", side: "right", terminal: "S2", offset: 1.4 },
    ],
  },
  // An AC adapter: an AC source as mains is (its mains side is in the
  // wall), S1 on top.
  adapter: {
    shape: "adapter",
    terminals: [
      { name: "S1", side: "top", terminal: "S1" },
      { name: "S2", side: "bottom", terminal: "S2" },
    ],
  },
});

/** Short type label shown in the symbol (params add volts/ohms/Hz in the view). */
const SHAPE_LABEL = Object.freeze({
  psu: "PSU",
  clock: "CLK",
  load: "LOAD",
  ac: "AC",
});

/** Which box edge a discrete pin sits on: the "return" pins (cathode / common /
    ground) exit RIGHT, everything else (anode / lead / contact) exits LEFT. */
function partPinSide(role) {
  return role === "cathode" || role === "common" || role === "gnd"
    ? "right"
    : "left";
}

/** A box symbol for a many-pinned discrete (a display / resistor array). */
function buildPartBox(def) {
  const sides = { left: [], right: [], top: [], bottom: [] };
  if (def.switchBank) {
    // A DIP bank reads as its n SWITCHES: pole k's A contact on the left row
    // paired with its B contact on the right (pin i faces pin 2n+1-i, the
    // same pairing internalBridges closes) — not 2n "contact" pins down one
    // edge, which drew an 8-position bank twice as tall as a 14-pin chip.
    const byPin = new Map(def.pins.map((p) => [p.n, p]));
    const n = def.pins.length / 2;
    for (let i = 1; i <= n; i++) {
      const a = byPin.get(i);
      const b = byPin.get(2 * n + 1 - i);
      sides.left.push({ kind: "pin", pin: a.n, name: a.name, role: a.role });
      sides.right.push({ kind: "pin", pin: b.n, name: b.name, role: b.role });
    }
    return {
      id: def.id,
      label: def.id,
      title: def.title,
      kind: "box",
      glyph: null,
      sides,
    };
  }
  for (const p of def.pins) {
    sides[partPinSide(p.role)].push({
      kind: "pin",
      pin: p.n,
      name: p.name,
      role: p.role,
    });
  }
  return {
    id: def.id,
    label: def.id,
    title: def.title,
    kind: "box",
    glyph: null,
    sides,
  };
}

/** The logical symbol for a discrete part or a desk brick (PSU / clock). */
export function buildPartSymbol(def) {
  const spec = SHAPE_DEFS[def.id];
  if (spec) {
    return {
      id: def.id,
      label: SHAPE_LABEL[def.id] ?? "",
      title: def.title,
      kind: "shape",
      shape: spec.shape,
      terminals: spec.terminals.map((t) => ({
        key: t.terminal != null ? `t:${t.terminal}` : `pin:${t.pin}`,
        name: t.name,
        side: t.side,
        offset: t.offset ?? 0,
        pin: t.pin ?? null,
        terminal: t.terminal ?? null,
      })),
    };
  }
  return buildPartBox(def);
}

/** Every chip's logical symbol, keyed by catalog id. */
export const SYMBOLS = new Map(
  CHIP_DEFS.map((def) => [def.id, buildSymbol(def)]),
);

/**
 * The logical symbol for ANY catalog id — chip, discrete, or brick — or null.
 * Chips are boxes with pin-role sides; discretes/bricks are distinctive shapes
 * (or boxes for the many-pinned displays).
 */
export function symbolFor(ref) {
  const cached = SYMBOLS.get(ref);
  if (cached) return cached;
  const chip = chipDef(ref);
  if (chip) return buildSymbol(chip);
  const part = partDef(ref);
  return part ? buildPartSymbol(part) : null;
}

/** Every stub of a symbol, flattened with its side tag. */
export function symbolStubs(symbol) {
  const out = [];
  for (const side of ["left", "right", "top", "bottom"]) {
    for (const stub of symbol.sides[side]) out.push({ ...stub, side });
  }
  return out;
}
